import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+()/\-.\s]{3,}$/;

/**
 * Wiederholbares Kontakt-Feld (Telefon oder E-Mail) mit optionalem Label + Validierung.
 * value: Array von { label, value }. onChange erhält das neue Array.
 */
export function ContactChannelsField({ type = "phone", label, items = [], onChange, testidPrefix }) {
  const isEmail = type === "email";
  const validate = (v) => {
    if (!v) return null;
    if (isEmail && !EMAIL_RE.test(v)) return "Ungültige E-Mail-Adresse";
    if (!isEmail && !PHONE_RE.test(v)) return "Ungültige Telefonnummer";
    return null;
  };

  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = () => onChange([...(items || []), { label: "", value: "" }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="ghost" size="sm" onClick={add} data-testid={`${testidPrefix}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" />Hinzufügen
        </Button>
      </div>
      {(!items || items.length === 0) && (
        <p className="text-xs text-muted-foreground italic" data-testid={`${testidPrefix}-empty`}>
          Noch keine {isEmail ? "E-Mail-Adresse" : "Telefonnummer"} hinterlegt.
        </p>
      )}
      <div className="space-y-2">
        {(items || []).map((it, idx) => {
          const err = validate(it.value);
          return (
            <div key={idx} className="flex gap-2 items-start" data-testid={`${testidPrefix}-row-${idx}`}>
              <Input
                value={it.label || ""}
                onChange={(e) => update(idx, { label: e.target.value })}
                placeholder="Bezeichnung"
                className="w-32 shrink-0"
                data-testid={`${testidPrefix}-label-${idx}`}
              />
              <div className="flex-1 min-w-0">
                <Input
                  type={isEmail ? "email" : "tel"}
                  value={it.value || ""}
                  onChange={(e) => update(idx, { value: e.target.value })}
                  placeholder={isEmail ? "name@firma.de" : "+49 …"}
                  className={err ? "border-destructive focus-visible:ring-destructive" : ""}
                  data-testid={`${testidPrefix}-value-${idx}`}
                  aria-invalid={!!err}
                />
                {err && <p className="text-xs text-destructive mt-1" data-testid={`${testidPrefix}-error-${idx}`}>{err}</p>}
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`${testidPrefix}-remove-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function hasInvalidChannels(items, type) {
  const re = type === "email" ? EMAIL_RE : PHONE_RE;
  return (items || []).some((it) => it.value && !re.test(it.value));
}
